import { Link } from 'react-router-dom';
import { categories, articles, faqItems } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function Home() {
  const featuredArticles = articles.filter((a) => a.featured);
  const latestArticles = articles.slice(0, 6);

  const breadcrumbItems = [
    { name: '首页', item: 'https://17c.com' },
    { name: '项目概览', item: 'https://17c.com/category/xiangmu-gailan' },
    { name: '使用教程', item: 'https://17c.com/category/shiyong-jiaocheng' },
  ];

  return (
    <>
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '17c.com GitHub 完整指南与资源汇总',
          description: '涵盖 17c.com GitHub 项目结构、使用教程、开源资源获取及常见问题解答。',
          url: 'https://17c.com',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems.map((item, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: item.name,
              item: item.item,
            })),
          },
        }}
      />
      <SchemaOrg type="BreadcrumbList" data={{ items: breadcrumbItems }} />
      <SchemaOrg type="FAQPage" data={{ questions: faqItems }} />

      <HeroSection />
      <FeaturesSection />
      <CategorySection categories={categories} />
      <FeaturedArticles articles={featuredArticles} />
      <LatestArticles articles={latestArticles} />
      <FAQSection faqItems={faqItems} />
      <CTASection />
    </>
  );
}

function HeroSection() {
  return (
    <section className="relative w-full min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20modern%20technology%20background%20with%20soft%20warm%20gradient%20tones%20of%20white%20cream%20and%20light%20amber%2C%20subtle%20geometric%20grid%20patterns%20overlay%2C%20minimalist%20clean%20aesthetic%2C%20very%20light%20and%20airy%20feel%2C%20no%20text%2C%20no%20people%2C%20smooth%20gradient%20surface&width=1920&height=800&seq=hero-bg-17c-01&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-background-50"></div>

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/80 backdrop-blur-sm border border-background-200/50 mb-6">
          <i className="ri-github-fill text-lg text-foreground-700"></i>
          <span className="text-sm text-foreground-600">GitHub 开源项目资源平台</span>
        </div>

        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight max-w-4xl mx-auto">
          17c.com GitHub
          <br />
          <span className="text-primary-300">完整指南与资源汇总</span>
        </h1>

        <p className="text-base md:text-lg text-background-200/80 max-w-2xl mx-auto mb-8 leading-relaxed">
          全面解析 17c.com GitHub 项目结构、使用教程、开源资源获取及常见问题解答，帮助开发者快速上手并充分利用 17c.com GitHub 上的相关工具与代码库。
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/category/shiyong-jiaocheng"
            className="w-full sm:w-auto px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            开始学习
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
          <Link
            to="/category/xiangmu-gailan"
            className="w-full sm:w-auto px-8 py-3 rounded-lg bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 font-medium text-sm hover:bg-background-50/30 transition-all whitespace-nowrap cursor-pointer"
          >
            项目概览
            <i className="ri-information-line ml-2"></i>
          </Link>
        </div>

        <div className="flex items-center justify-center gap-8 mt-12">
          {[
            { value: '12+', label: '教程文章' },
            { value: '6', label: '分类专题' },
            { value: '50K+', label: '月访问量' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
                {stat.value}
              </div>
              <div className="text-xs text-background-300/80 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: 'ri-file-code-line',
      title: '项目结构全解析',
      desc: '深入了解 17c.com GitHub 项目的目录结构、模块划分与设计理念。',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: '从零到一教程',
      desc: '手把手教学，从环境搭建到项目部署，覆盖全流程。',
    },
    {
      icon: 'ri-database-2-line',
      title: '开源资源汇总',
      desc: '精心整理的工具库、代码片段与项目模板一站式获取。',
    },
    {
      icon: 'ri-question-answer-line',
      title: 'FAQ 常见问题',
      desc: '高频问题集中解答，快速解决你遇到的各种疑难杂症。',
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            为什么选择 17c.com GitHub
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto">
            我们提供最全面的 GitHub 开源项目资源，帮助你高效学习和开发
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <div
              key={f.title}
              className="group p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200/60 transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                <i className={`${f.icon} text-xl`}></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategorySection({ categories }: { categories: typeof import('@/mocks/articles').categories }) {
  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            内容分类
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto">
            按专题浏览，快速找到你需要的资源
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all shrink-0">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1 group-hover:text-primary-500 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                    {cat.description}
                  </p>
                  <span className="text-xs text-foreground-400">
                    {cat.articleCount} 篇文章
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedArticles({ articles }: { articles: typeof import('@/mocks/articles').articles }) {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              精选文章
            </h2>
            <p className="text-foreground-600 text-sm">编辑推荐的高质量内容</p>
          </div>
          <Link
            to="/category/shiyong-jiaocheng"
            className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {articles.map((article) => {
            const category = categories.find((c) => c.id === article.categoryId);
            return (
              <ArticleCard
                key={article.id}
                {...article}
                categoryName={category?.name || ''}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LatestArticles({ articles }: { articles: typeof import('@/mocks/articles').articles }) {
  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              最新文章
            </h2>
            <p className="text-foreground-600 text-sm">持续更新的技术内容</p>
          </div>
          <Link
            to="/category/shiyong-jiaocheng"
            className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {articles.map((article) => {
            const category = categories.find((c) => c.id === article.categoryId);
            return (
              <ArticleCard
                key={article.id}
                {...article}
                categoryName={category?.name || ''}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FAQSection({ faqItems }: { faqItems: typeof import('@/mocks/articles').faqItems }) {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            常见问题
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto">
            关于 17c.com GitHub 项目的常见疑问解答
          </p>
        </div>

        <div className="max-w-3xl mx-auto divide-y divide-background-200/70">
          {faqItems.map((faq, index) => (
            <details key={index} className="group py-5 cursor-pointer">
              <summary className="flex items-center justify-between font-medium text-foreground-800 hover:text-primary-600 transition-colors list-none">
                <span className="pr-4 text-sm md:text-base">{faq.question}</span>
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 group-open:bg-primary-100 shrink-0 transition-colors">
                  <i className="ri-add-line text-sm group-open:hidden"></i>
                  <i className="ri-subtract-line text-sm hidden group-open:block text-primary-600"></i>
                </span>
              </summary>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed pl-0 pr-10">
                {faq.answer}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative py-20 md:py-24 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20pattern%20with%20soft%20amber%20and%20cream%20tones%2C%20smooth%20flowing%20curves%2C%20minimalist%20modern%20design%2C%20light%20airy%20atmosphere%2C%20professional%20tech%20feel%2C%20no%20text%2C%20no%20people&width=1920&height=600&seq=cta-bg-17c-02&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-900/85 to-background-900/70"></div>

      <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
          开始使用 17c.com GitHub 资源
        </h2>
        <p className="text-background-300 max-w-lg mx-auto mb-8 text-sm md:text-base">
          浏览我们的教程和文档，快速掌握 17c.com GitHub 项目的使用方法
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/category/shiyong-jiaocheng"
            className="w-full sm:w-auto px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            浏览教程
          </Link>
          <a
            href="https://github.com"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="w-full sm:w-auto px-8 py-3 rounded-lg bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/20 font-medium text-sm hover:bg-background-50/25 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-github-fill mr-2"></i>
            GitHub 仓库
          </a>
        </div>
      </div>
    </section>
  );
}